<!--
============================================================
ByteMatrix Co., Ltd.
https://www.bytematrix.co.th | contact@bytematrix.co.th
============================================================
Module   : eCommerce Product Management
File     : importProduct.vue
Author   : Patcharapong Ponzue <contact@ibig.dev>
Created  : 2026-08-26
Updated  : 2026-08-26
============================================================
Description:
  Enhanced Multi-Page & Bulk Product Import View for Back-Office.
  Supports Page Range auto-pagination, Multi-URL list batch fetching,
  single URL/XML, JSON file upload, smart deduplication, and
  real-time WebSocket progress reporting.
============================================================
-->

<template>
  <div v-if="$ability.can('read', this.$route.meta.resource)">
    <!-- Main Import Configuration Card -->
    <b-overlay
      :show="show || isFetching"
      rounded="sm"
    >
      <b-card
        no-body
        class="mb-2"
      >
        <b-card-header class="pb-1 d-flex justify-content-between align-items-center">
          <div>
            <h4 class="card-title mb-25">
              <feather-icon
                icon="DownloadCloudIcon"
                size="20"
                class="mr-50 text-primary align-middle"
              />
              {{ $t('Import Product') || 'นำเข้าสินค้า' }}
            </h4>
            <b-card-text class="text-muted font-small-3 mb-0">
              {{ $t('IMPORT_MULTI_PAGE_DESC') || 'ดึงและนำเข้าข้อมูลสินค้าได้ทีละหลายหน้าพร้อมกันจาก Web API, XML หรือไฟล์ JSON' }}
            </b-card-text>
          </div>
          <div>
            <b-badge
              variant="light-primary"
              class="badge-glow"
            >
              <feather-icon
                icon="LayersIcon"
                size="12"
                class="mr-25"
              />
              Multi-Page Engine v2.0
            </b-badge>
          </div>
        </b-card-header>

        <b-card-body>
          <!-- Import Modes Tabs -->
          <b-tabs
            v-model="activeTab"
            pills
            nav-class="mb-2"
          >
            <!-- Tab 1: Page Range Mode -->
            <b-tab
              active
              :title-item-class="'font-weight-bold'"
            >
              <template #title>
                <feather-icon
                  icon="LayersIcon"
                  class="mr-50"
                />
                <span>{{ $t('PAGE_RANGE_MODE') || 'ช่วงหน้าอัตโนมัติ (Page Range)' }}</span>
              </template>

              <b-alert
                variant="primary"
                show
                class="mb-2"
              >
                <div class="alert-body font-small-3">
                  <feather-icon
                    icon="InfoIcon"
                    class="mr-50"
                  />
                  <span>
                    {{ $t('PAGE_RANGE_TIP') || 'ระบุ URL Template โดยใช้ตัวแปร {page} เช่น: https://api.example.com/api?cmd=getproducts&page={page}&token=... ระบบจะทำการแทนที่ {page} ด้วยเลขหน้าตั้งแต่หน้าเริ่มต้นถึงหน้าสิ้นสุดอัตโนมัติ' }}
                  </span>
                </div>
              </b-alert>

              <b-row>
                <!-- URL Template -->
                <b-col
                  cols="12"
                  md="8"
                >
                  <b-form-group
                    :label="($t('URL_TEMPLATE') || 'URL Template') + ' :'"
                    label-for="input-range-url"
                  >
                    <b-form-input
                      id="input-range-url"
                      v-model="rangeConfig.urlTemplate"
                      placeholder="https://api.gooapp.me/gooweb/api_startapp?site_id=34186&cmd=getproducts&page={page}&token=..."
                      :disabled="isFetching"
                    />
                  </b-form-group>
                </b-col>

                <!-- Data Format -->
                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group
                    :label="($t('DATA_FORMAT') || 'รูปแบบข้อมูล') + ' :'"
                    label-for="select-range-format"
                  >
                    <v-select
                      id="select-range-format"
                      v-model="rangeConfig.format"
                      :options="formatOptions"
                      :reduce="opt => opt.value"
                      :clearable="false"
                      :disabled="isFetching"
                    />
                  </b-form-group>
                </b-col>

                <!-- Start Page -->
                <b-col
                  cols="6"
                  md="3"
                >
                  <b-form-group
                    :label="($t('START_PAGE') || 'หน้าเริ่มต้น') + ' :'"
                    label-for="input-start-page"
                  >
                    <b-form-input
                      id="input-start-page"
                      v-model.number="rangeConfig.startPage"
                      type="number"
                      min="1"
                      :disabled="isFetching"
                    />
                  </b-form-group>
                </b-col>

                <!-- End Page -->
                <b-col
                  cols="6"
                  md="3"
                >
                  <b-form-group
                    :label="($t('END_PAGE') || 'หน้าสิ้นสุด') + ' :'"
                    label-for="input-end-page"
                  >
                    <b-form-input
                      id="input-end-page"
                      v-model.number="rangeConfig.endPage"
                      type="number"
                      min="1"
                      :disabled="isFetching"
                    />
                  </b-form-group>
                </b-col>

                <!-- Concurrency Limit -->
                <b-col
                  cols="12"
                  md="3"
                >
                  <b-form-group
                    :label="($t('CONCURRENCY') || 'จำนวนดึงพร้อมกัน (Concurrency)') + ' :'"
                    label-for="input-range-concurrency"
                  >
                    <b-form-input
                      id="input-range-concurrency"
                      v-model.number="rangeConfig.concurrency"
                      type="number"
                      min="1"
                      max="10"
                      :disabled="isFetching"
                    />
                  </b-form-group>
                </b-col>

                <!-- Fetch Button -->
                <b-col
                  cols="12"
                  md="3"
                  class="d-flex align-items-end mb-1"
                >
                  <b-button
                    v-if="!isFetching"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    block
                    @click="fetchPageRange"
                  >
                    <feather-icon
                      icon="DownloadCloudIcon"
                      class="mr-50"
                    />
                    <span>{{ $t('FETCH_ALL_PAGES') || 'ดึงข้อมูลทุกหน้า' }} ({{ calculatedTotalRangePages }} {{ $t('PAGES') || 'หน้า' }})</span>
                  </b-button>
                  <b-button
                    v-else
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="danger"
                    block
                    @click="stopFetching"
                  >
                    <b-spinner
                      small
                      class="mr-50"
                    />
                    <span>{{ $t('STOP_FETCHING') || 'หยุดการดึงข้อมูล' }}</span>
                  </b-button>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Tab 2: Multi-URL List Mode -->
            <b-tab :title-item-class="'font-weight-bold'">
              <template #title>
                <feather-icon
                  icon="ListIcon"
                  class="mr-50"
                />
                <span>{{ $t('MULTI_URL_MODE') || 'หลายลิงก์ (Multi-URL List)' }}</span>
              </template>

              <b-alert
                variant="primary"
                show
                class="mb-2"
              >
                <div class="alert-body font-small-3">
                  <feather-icon
                    icon="InfoIcon"
                    class="mr-50"
                  />
                  <span>
                    {{ $t('MULTI_URL_TIP') || 'วาง URL หลายรายการแยกกันทีละบรรทัด ระบบจะทำการดึงข้อมูลจากทุก URL และนำมารวมกันในตารางเดียว' }}
                  </span>
                </div>
              </b-alert>

              <b-row>
                <b-col cols="12">
                  <b-form-group
                    :label="($t('URL_LIST') || 'รายการ URL (บรรทัดละ 1 ลิงก์)') + ' :'"
                    label-for="input-multi-urls"
                  >
                    <b-form-textarea
                      id="input-multi-urls"
                      v-model="multiUrlConfig.urlListText"
                      rows="4"
                      placeholder="https://api.gooapp.me/gooweb/api_startapp?site_id=34186&cmd=getproducts&page=1&#10;https://api.gooapp.me/gooweb/api_startapp?site_id=34186&cmd=getproducts&page=2&#10;https://www.quinl.com/.../product-xml.php?page=1"
                      :disabled="isFetching"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group
                    :label="($t('DATA_FORMAT') || 'รูปแบบข้อมูล') + ' :'"
                    label-for="select-multi-format"
                  >
                    <v-select
                      id="select-multi-format"
                      v-model="multiUrlConfig.format"
                      :options="multiFormatOptions"
                      :reduce="opt => opt.value"
                      :clearable="false"
                      :disabled="isFetching"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group
                    :label="($t('CONCURRENCY') || 'จำนวนดึงพร้อมกัน') + ' :'"
                    label-for="input-multi-concurrency"
                  >
                    <b-form-input
                      id="input-multi-concurrency"
                      v-model.number="multiUrlConfig.concurrency"
                      type="number"
                      min="1"
                      max="10"
                      :disabled="isFetching"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="d-flex align-items-end mb-1"
                >
                  <b-button
                    v-if="!isFetching"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    block
                    @click="fetchMultiUrlList"
                  >
                    <feather-icon
                      icon="DownloadCloudIcon"
                      class="mr-50"
                    />
                    <span>{{ $t('FETCH_ALL_URLS') || 'ดึงข้อมูลจากทุก URL' }} ({{ calculatedMultiUrlCount }} {{ $t('LINKS') || 'ลิงก์' }})</span>
                  </b-button>
                  <b-button
                    v-else
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="danger"
                    block
                    @click="stopFetching"
                  >
                    <b-spinner
                      small
                      class="mr-50"
                    />
                    <span>{{ $t('STOP_FETCHING') || 'หยุดการดึงข้อมูล' }}</span>
                  </b-button>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Tab 3: Single URL / JSON File Mode -->
            <b-tab :title-item-class="'font-weight-bold'">
              <template #title>
                <feather-icon
                  icon="FileTextIcon"
                  class="mr-50"
                />
                <span>{{ $t('SINGLE_URL_MODE') || 'หน้าเดี่ยว / ไฟล์ JSON' }}</span>
              </template>

              <b-row>
                <b-col cols="12">
                  <b-form-group
                    label-cols="2"
                    label-cols-lg="2"
                    label="IMPORT WEB (JSON) :"
                    label-for="input-single-json"
                  >
                    <b-input-group>
                      <b-form-input
                        id="input-single-json"
                        v-model="inputUrl"
                        placeholder="URL IMPORT JSON"
                        :disabled="isFetching"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-primary"
                          :disabled="isFetching"
                          @click="getData"
                        >
                          <span class="mr-25 align-middle">{{ $t('GET_FROM_URL') }}</span>
                          <feather-icon
                            icon="GlobeIcon"
                            size="12"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>

                  <b-form-group
                    label-cols="2"
                    label-cols-lg="2"
                    label="IMPORT WEB (XML) :"
                    label-for="input-single-xml"
                  >
                    <b-input-group>
                      <b-form-input
                        id="input-single-xml"
                        v-model="inputUrlXML"
                        placeholder="URL IMPORT XML"
                        :disabled="isFetching"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-primary"
                          :disabled="isFetching"
                          @click="getDataXML"
                        >
                          <span class="mr-25 align-middle">{{ $t('GET_FROM_URL') }}</span>
                          <feather-icon
                            icon="GlobeIcon"
                            size="12"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>

                  <b-form-group
                    label-cols="2"
                    label-cols-lg="2"
                    label="IMPORT JSON FILE :"
                    label-for="input-file-json"
                  >
                    <b-form-file
                      id="input-file-json"
                      ref="uploadFileJson"
                      class="mb-1"
                      accept=".json"
                      :placeholder="$t('CHOOSE_JSON_FILE') || 'เลือกไฟล์ .json หรือลากวางที่นี่...'"
                      :drop-placeholder="$t('DROP_FILE_HERE') || 'วางไฟล์ที่นี่...'"
                      @change="updateFiles"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>
          </b-tabs>

          <!-- Fetch Progress Feedback -->
          <div
            v-if="isFetching || fetchSummary.show"
            class="mt-1"
          >
            <!-- Progress Bar while Fetching -->
            <div
              v-if="isFetching"
              class="mb-2"
            >
              <div class="d-flex justify-content-between align-items-center mb-50">
                <span class="font-weight-bold text-primary">
                  <b-spinner
                    small
                    class="mr-50"
                  />
                  {{ $t('FETCHING_PAGE') || 'กำลังดึงข้อมูล' }}: {{ fetchProgress.current }} / {{ fetchProgress.total }}
                  ({{ $t('SUCCESS_PAGES') || 'สำเร็จ' }} {{ fetchProgress.successPages }}, {{ $t('FAILED_PAGES') || 'ล้มเหลว' }} {{ fetchProgress.failedPages }})
                </span>
                <span class="font-small-3 text-muted">
                  {{ $t('FOUND_PRODUCTS') || 'พบสินค้าสะสม' }}: <strong>{{ fetchProgress.foundProducts }}</strong> {{ $t('List') }}
                </span>
              </div>
              <b-progress
                :max="fetchProgress.total"
                height="12px"
                animated
                variant="primary"
                striped
              >
                <b-progress-bar :value="fetchProgress.current">
                  <strong>{{ Math.round((fetchProgress.current / (fetchProgress.total || 1)) * 100) }}%</strong>
                </b-progress-bar>
              </b-progress>
              <small class="text-muted mt-25 d-block text-truncate">
                URL: {{ fetchProgress.currentUrl }}
              </small>
            </div>

            <!-- Fetch Summary Stats Card -->
            <b-alert
              v-if="fetchSummary.show && !isFetching"
              variant="success"
              show
              class="mb-2"
            >
              <div class="alert-body">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="20"
                      class="mr-1 text-success"
                    />
                    <div>
                      <h6 class="alert-heading mb-25 text-success font-weight-bolder">
                        {{ $t('FETCH_COMPLETE') || 'ดึงข้อมูลสำเร็จเรียบร้อย!' }}
                      </h6>
                      <span class="font-small-3">
                        {{ $t('FETCH_STATS_PAGES') || 'ดึงทั้งหมด' }} <strong>{{ fetchSummary.totalPages }}</strong> {{ $t('PAGES') || 'หน้า' }}
                        (สำเร็จ {{ fetchSummary.successPages }}, ล้มเหลว {{ fetchSummary.failedPages }}) &bull;
                        {{ $t('TOTAL_RAW_ITEMS') || 'พบสินค้าทั้งหมด' }} <strong>{{ fetchSummary.rawTotal }}</strong> {{ $t('List') }} &bull;
                        {{ $t('DUPLICATES_REMOVED') || 'ตัดรายการซ้ำ' }} <b-badge variant="light-warning">{{ fetchSummary.duplicateCount }}</b-badge> &bull;
                        {{ $t('UNIQUE_READY') || 'พร้อมนำเข้า' }} <b-badge variant="light-success">{{ fetchSummary.uniqueCount }}</b-badge>
                      </span>
                    </div>
                  </div>
                  <b-button
                    size="sm"
                    variant="outline-success"
                    class="mt-50 mt-sm-0"
                    @click="fetchSummary.show = false"
                  >
                    {{ $t('Dismiss') || 'ปิดการแจ้งเตือน' }}
                  </b-button>
                </div>
              </div>
            </b-alert>
          </div>

          <!-- Bottom Action Controls -->
          <div class="d-flex justify-content-between align-items-center flex-wrap pt-50 border-top">
            <div>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-warning"
                size="sm"
                @click="clearData"
              >
                <feather-icon
                  icon="RefreshIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('Reset') }}</span>
              </b-button>
            </div>
            <div class="font-small-3 text-muted">
              {{ $t('TOTAL_LOADED') || 'โหลดแล้วทั้งหมด' }}: <strong class="text-primary">{{ queriedItems }}</strong> {{ $t('List') }} |
              {{ $t('SELECTED') }}: <strong class="text-success">{{ selected.length }}</strong> {{ $t('List') }}
            </div>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>

    <!-- DB Import WebSocket Progress Bars -->
    <div
      v-if="valueProduct !== 0"
      class="mb-1"
    >
      <div class="d-flex justify-content-between align-items-center mb-25">
        <label class="font-weight-bold mb-0">
          <feather-icon
            icon="LayersIcon"
            size="14"
            class="mr-25 text-primary"
          />
          {{ $t('Product') }} {{ valueProduct }} / {{ maxProduct }} ({{ nameProduct }})
        </label>
        <span class="font-small-3 text-muted">{{ Math.round((valueProduct / (maxProduct || 1)) * 100) }}%</span>
      </div>
      <b-progress
        :max="maxProduct"
        height="12px"
        animated
        striped
        variant="primary"
      >
        <b-progress-bar :value="valueProduct">
          <strong>{{ valueProduct }} / {{ maxProduct }}</strong>
        </b-progress-bar>
      </b-progress>
    </div>

    <div
      v-if="valueImage !== 0"
      class="mb-2"
    >
      <div class="d-flex justify-content-between align-items-center mb-25">
        <label class="font-weight-bold mb-0">
          <feather-icon
            icon="ImageIcon"
            size="14"
            class="mr-25 text-info"
          />
          {{ $t('Image') }} {{ valueImage }} / {{ maxImage }} ({{ nameImage }})
        </label>
        <span class="font-small-3 text-muted">{{ Math.round((valueImage / (maxImage || 1)) * 100) }}%</span>
      </div>
      <b-progress
        :max="maxImage"
        height="12px"
        class="progress-bar-info"
        animated
        striped
        variant="info"
      >
        <b-progress-bar
          :value="valueImage"
          variant="info"
        >
          <strong>{{ valueImage }} / {{ maxImage }}</strong>
        </b-progress-bar>
      </b-progress>
    </div>

    <!-- Product Preview & Selection Table -->
    <b-overlay
      :show="show"
      rounded="sm"
    >
      <b-card
        no-body
        class="mb-0"
      >
        <div class="m-2">
          <b-row class="align-items-center">
            <!-- Left: Page Size & Source Page Filter -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center flex-wrap"
            >
              <div class="d-flex align-items-center mr-2 mb-1 mb-md-0">
                <label class="mb-0 mr-50">{{ $t('Show') }}</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector"
                  style="width: 85px;"
                  @input="pageSize"
                />
              </div>

              <!-- Filter by Source Page -->
              <div
                v-if="availableSourcePages.length > 1"
                class="d-flex align-items-center mb-1 mb-md-0"
              >
                <label class="mb-0 mr-50 font-small-3 text-muted">{{ $t('FILTER_PAGE') || 'กรองตามหน้า' }}:</label>
                <v-select
                  v-model="selectedSourcePageFilter"
                  :options="sourcePageFilterOptions"
                  label="label"
                  :reduce="opt => opt.value"
                  :clearable="false"
                  style="min-width: 140px;"
                />
              </div>
            </b-col>

            <!-- Right: Action Buttons & Search -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end flex-wrap">
                <!-- Search input within table -->
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1 mb-1 mb-sm-0"
                  style="max-width: 180px;"
                  :placeholder="$t('Search') + '...'"
                />

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="success"
                  size="sm"
                  class="mr-1 mb-1 mb-sm-0"
                  :disabled="filteredData.length === 0"
                  @click="selectAllRows"
                >
                  <feather-icon
                    icon="CheckSquareIcon"
                    class="mr-25"
                  />
                  <span>{{ $t('SELECT_ALL') }} ({{ filteredData.length }})</span>
                </b-button>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="outline-warning"
                  size="sm"
                  class="mr-1 mb-1 mb-sm-0"
                  :disabled="selected.length === 0"
                  @click="clearSelected"
                >
                  <feather-icon
                    icon="XIcon"
                    class="mr-25"
                  />
                  <span>{{ $t('CLEAR_SELECTED') }}</span>
                </b-button>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  size="sm"
                  class="mr-1 mb-1 mb-sm-0"
                  :disabled="selected.length === 0"
                  @click="importData"
                >
                  <feather-icon
                    icon="PlusIcon"
                    class="mr-25"
                  />
                  <span>{{ $t('IMPORT') }} ({{ selected.length }})</span>
                </b-button>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="outline-primary"
                  size="sm"
                  class="mb-1 mb-sm-0"
                  :disabled="respData.length === 0"
                  @click="importAllProduct"
                >
                  <feather-icon
                    icon="DownloadCloudIcon"
                    class="mr-25"
                  />
                  <span>{{ $t('IMPORT_ALL') }} ({{ respData.length }})</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Table Component -->
        <b-table
          ref="selectableTable"
          striped
          responsive
          hover
          class="position-relative mb-0"
          :items="paginatedData"
          :fields="tableColumns"
          primary-key="products_id"
          show-empty
          :empty-text="$t('No matching records found')"
          style="min-height: 350px; max-height: 60vh;"
        >
          <!-- Head Checkbox (Select all on current visible page) -->
          <template #head(selected)>
            <b-form-checkbox
              :checked="isAllCurrentPageSelected"
              :indeterminate="isCurrentPagePartiallySelected"
              @change="toggleSelectCurrentPage"
            />
          </template>

          <!-- Checkbox per row -->
          <template #cell(selected)="data">
            <b-form-checkbox
              :checked="isRowSelected(data.item)"
              @change="toggleRowSelection(data.item)"
            />
          </template>

          <!-- Column: Images -->
          <template #cell(products_image)="data">
            <div
              v-if="data.item.products_image && data.item.products_image.length > 0"
              class="d-flex align-items-center"
            >
              <b-img
                :src="getImage(data.item.products_image[0])"
                fluid
                thumbnail
                rounded
                width="60"
                height="60"
                style="object-fit: cover; max-height: 60px;"
                :alt="'Product_' + data.item.products_id"
              />
              <span
                v-if="data.item.products_image.length > 1"
                class="badge badge-light-secondary ml-50 font-small-2"
              >
                +{{ data.item.products_image.length - 1 }}
              </span>
            </div>
            <span
              v-else
              class="text-muted font-small-2"
            >
              {{ $t('NO_IMAGE') || 'ไม่มีรูป' }}
            </span>
          </template>

          <!-- Column: ID -->
          <template #cell(products_id)="data">
            <span class="font-weight-bold text-primary font-small-3">
              {{ data.item.products_id }}
            </span>
          </template>

          <!-- Column: Name -->
          <template #cell(productslang_name)="data">
            <div class="font-weight-bold text-dark font-small-3">
              {{ data.item.productslang_name }}
            </div>
            <small
              v-if="data.item.productslang_detail"
              class="text-muted text-truncate d-block"
              style="max-width: 320px;"
            >
              {{ stripHtml(data.item.productslang_detail) }}
            </small>
          </template>

          <!-- Column: Source Page -->
          <template #cell(source_page)="data">
            <b-badge
              variant="light-info"
              pill
              class="font-small-2"
            >
              <feather-icon
                icon="LayersIcon"
                size="10"
                class="mr-25"
              />
              {{ data.item.source_page || 'Page 1' }}
            </b-badge>
          </template>

          <!-- Column: Category -->
          <template #cell(productpcategory_pcategory_id)="data">
            <span class="font-small-3 text-secondary">
              {{ data.item.productpcategory_pcategory_id }}
            </span>
          </template>

          <!-- Column: Price -->
          <template #cell(products_price)="data">
            <span class="font-weight-bolder text-success font-small-3">
              ฿{{ Number(data.item.products_price || 0).toLocaleString() }}
            </span>
          </template>

          <!-- Column: Weight -->
          <template #cell(products_weight)="data">
            <span class="font-small-3 text-muted">
              {{ data.item.products_weight || 0 }} g.
            </span>
          </template>
        </b-table>

        <!-- Pagination Controls Footer -->
        <div class="mx-2 my-1">
          <b-row class="align-items-center">
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted font-small-3">
                {{ $t('Showing') || 'แสดง' }}
                {{ filteredData.length > 0 ? (currentPage - 1) * perPage + 1 : 0 }} -
                {{ Math.min(currentPage * perPage, filteredData.length) }}
                {{ $t('of') }} {{ filteredData.length }} {{ $t('List') }}
                <span
                  v-if="selected.length > 0"
                  class="text-primary font-weight-bold ml-50"
                >
                  ({{ $t('SELECTED') }}: {{ selected.length }})
                </span>
              </span>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              class="d-flex justify-content-center justify-content-sm-end mt-1 mt-sm-0"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredData.length"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0"
                prev-class="prev-item"
                next-class="next-item"
                @input="nextPage"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BCardText,
  BRow,
  BCol,
  BImg,
  BPagination,
  BFormInput,
  BFormTextarea,
  BInputGroup,
  BInputGroupAppend,
  BFormGroup,
  BFormFile,
  BFormCheckbox,
  BButton,
  BTable,
  BOverlay,
  BProgress,
  BProgressBar,
  BBadge,
  BAlert,
  BTabs,
  BTab,
  BSpinner,
  VBModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import perPageOptions from '@/perPageOptions'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { avatarText } from '@core/utils/filter'
import storeModule from '@/store/services/eCommerce/product'
import axios from 'axios'
import { parseStringPromise } from 'xml2js'

const STORE_MODULE_NAME = 'product'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardText,
    BRow,
    BCol,
    BImg,
    BPagination,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BFormGroup,
    BFormFile,
    BFormCheckbox,
    BButton,
    BTable,
    BOverlay,
    BProgress,
    BProgressBar,
    BBadge,
    BAlert,
    BTabs,
    BTab,
    BSpinner,
    vSelect,
  },
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  data() {
    return {
      avatarText,
      activeTab: 0,

      // Page Range Mode Config
      rangeConfig: {
        urlTemplate: 'https://api.gooapp.me/gooweb/api_startapp?site_id=34186&cmd=getproducts&page={page}&token=44eeWqqaasggGHasdfasdfas',
        format: 'json',
        startPage: 1,
        endPage: 5,
        concurrency: 3,
      },
      formatOptions: [
        { label: 'JSON API (GooApp / REST)', value: 'json' },
        { label: 'XML Data (QuinL / RSS)', value: 'xml' },
      ],

      // Multi-URL Mode Config
      multiUrlConfig: {
        urlListText: '',
        format: 'auto',
        concurrency: 3,
      },
      multiFormatOptions: [
        { label: 'Auto Detect (ตรวจจับอัตโนมัติ)', value: 'auto' },
        { label: 'JSON API', value: 'json' },
        { label: 'XML Data', value: 'xml' },
      ],

      // Single Mode Config (Legacy Support)
      inputUrl: 'https://api.gooapp.me/gooweb/api_startapp?site_id=34186&cmd=getproducts&token=44eeWqqaasggGHasdfasdfas',
      inputUrlXML: 'https://www.quinl.com/v3_template_verified/freedom/product-xml.php?memberid=33695&lang=th&header=xml',

      // Multi-Page Fetching Progress State
      isFetching: false,
      abortFetch: false,
      fetchProgress: {
        current: 0,
        total: 0,
        currentUrl: '',
        foundProducts: 0,
        successPages: 0,
        failedPages: 0,
      },
      fetchSummary: {
        show: false,
        totalPages: 0,
        successPages: 0,
        failedPages: 0,
        rawTotal: 0,
        duplicateCount: 0,
        uniqueCount: 0,
      },

      // Data Table & Filter State
      respData: [],
      selected: [],
      searchQuery: '',
      selectedSourcePageFilter: 'all',
      perPageOptions,
      show: false,
      queriedItems: 0,
      currentPage: 1,
      perPage: localStorage.getItem('itemsPerPage')
        ? parseInt(localStorage.getItem('itemsPerPage'), 10)
        : 50,

      // WebSocket DB Import Progress
      nameProduct: '',
      valueProduct: 0,
      maxProduct: 0,
      nameImage: '',
      valueImage: 0,
      maxImage: 0,
    }
  },
  computed: {
    calculatedTotalRangePages() {
      const start = parseInt(this.rangeConfig.startPage, 10) || 1
      const end = parseInt(this.rangeConfig.endPage, 10) || 1
      return Math.max(1, end - start + 1)
    },
    calculatedMultiUrlCount() {
      if (!this.multiUrlConfig.urlListText) return 0
      return this.multiUrlConfig.urlListText
        .split('\n')
        .map(u => u.trim())
        .filter(u => u.length > 0).length
    },
    availableSourcePages() {
      const pages = new Set()
      this.respData.forEach(item => {
        if (item.source_page) {
          pages.add(item.source_page)
        }
      })
      return Array.from(pages).sort((a, b) => {
        const numA = parseInt(a.replace(/\D/g, ''), 10) || 0
        const numB = parseInt(b.replace(/\D/g, ''), 10) || 0
        return numA - numB
      })
    },
    sourcePageFilterOptions() {
      const opts = [{ label: `${this.$t('ALL_PAGES') || 'ทุกหน้า'} (${this.respData.length})`, value: 'all' }]
      this.availableSourcePages.forEach(p => {
        const count = this.respData.filter(i => i.source_page === p).length
        opts.push({ label: `${p} (${count})`, value: p })
      })
      return opts
    },
    filteredData() {
      let list = this.respData

      // Filter by source page
      if (this.selectedSourcePageFilter !== 'all') {
        list = list.filter(item => item.source_page === this.selectedSourcePageFilter)
      }

      // Filter by search query
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const q = this.searchQuery.trim().toLowerCase()
        list = list.filter(item => {
          const name = String(item.productslang_name || '').toLowerCase()
          const id = String(item.products_id || '').toLowerCase()
          const cat = String(item.productpcategory_pcategory_id || '').toLowerCase()
          return name.includes(q) || id.includes(q) || cat.includes(q)
        })
      }

      return list
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredData.slice(start, start + this.perPage)
    },
    isAllCurrentPageSelected() {
      if (this.paginatedData.length === 0) return false
      return this.paginatedData.every(item => this.isRowSelected(item))
    },
    isCurrentPagePartiallySelected() {
      if (this.paginatedData.length === 0) return false
      const some = this.paginatedData.some(item => this.isRowSelected(item))
      return some && !this.isAllCurrentPageSelected
    },
    tableColumns() {
      return [
        {
          key: 'selected', label: '', sortable: false, thStyle: { width: '40px' },
        },
        {
          key: 'products_image', label: this.$t('IMAGE') || 'รูปภาพ', sortable: false, thStyle: { width: '80px' },
        },
        { key: 'products_id', label: this.$t('ID') || 'รหัสสินค้า', sortable: true },
        { key: 'productslang_name', label: this.$t('NAME') || 'ชื่อสินค้า', sortable: false },
        { key: 'source_page', label: this.$t('SOURCE_PAGE') || 'หน้าต้นทาง', sortable: true },
        { key: 'productpcategory_pcategory_id', label: this.$t('ID_CATEGORY') || 'หมวดหมู่', sortable: true },
        { key: 'products_price', label: this.$t('PRICE') || 'ราคา', sortable: false },
        { key: 'products_weight', label: this.$t('WEIGHT') || 'น้ำหนัก', sortable: false },
      ]
    },
    wsClient() {
      return store.state.websockets.wsClient[STORE_MODULE_NAME]
    },
  },
  created() {
    if (!store.hasModule(STORE_MODULE_NAME)) {
      store.registerModule(STORE_MODULE_NAME, storeModule)
    }
    const userData = JSON.parse(localStorage.getItem('userData')) || {}
    const applicationId = localStorage.getItem('applicationId') || ''
    const pathname = `/Products?type=${userData.userType}&userId=${userData.userId}&applicationId=${applicationId}`
    const params = {
      pathname,
      page: STORE_MODULE_NAME,
    }
    store.dispatch('websockets/connect', params)
  },
  destroyed() {
    if (store.hasModule(STORE_MODULE_NAME)) {
      store.unregisterModule(STORE_MODULE_NAME)
    }
    store.dispatch('websockets/disconnect', STORE_MODULE_NAME)
  },
  beforeMount() {
    if (this.wsClient) {
      this.wsClient.onerror = err => {
        console.error('WS Error:', err)
      }
      this.wsClient.onclose = () => {
        console.log('WS Closed')
      }
      this.wsClient.onmessage = msg => {
        try {
          const SocketMessage = JSON.parse(msg.data)
          if (SocketMessage.data && SocketMessage.data.send) {
            if (SocketMessage.data.send.data && SocketMessage.data.send.data.message === 'IMPORT_PRODUCT_IMAGE') {
              this.show = true
              const payload = SocketMessage.data.send.data.data
              this.nameImage = payload.nameImage || ''
              this.valueImage = payload.currentImage || 0
              this.maxImage = payload.maxImage || 0
              this.nameProduct = payload.nameProduct || ''
              this.valueProduct = payload.currentProduct || 0
              this.maxProduct = payload.maxProduct || 0
              if (this.valueProduct >= this.maxProduct && this.maxProduct > 0) {
                this.show = false
              }
            }
          }
        } catch (e) {
          console.error('WS parse error', e)
        }
      }
    }
  },
  methods: {
    // Utility for image URLs
    getImage(item) {
      if (!item) return ''
      const str = String(item)
      if (str.startsWith('http://') || str.startsWith('https://')) {
        return str
      }
      return `https://gooapp.quinlcdn.com/${str}`
    },
    stripHtml(html) {
      if (!html) return ''
      return String(html).replace(/<[^>]*>?/gm, '').trim()
    },

    // Row Selection Methods
    isRowSelected(item) {
      return this.selected.some(s => String(s.products_id) === String(item.products_id))
    },
    toggleRowSelection(item) {
      const idx = this.selected.findIndex(s => String(s.products_id) === String(item.products_id))
      if (idx > -1) {
        this.selected.splice(idx, 1)
      } else {
        this.selected.push(item)
      }
    },
    toggleSelectCurrentPage(checked) {
      if (checked) {
        this.paginatedData.forEach(item => {
          if (!this.isRowSelected(item)) {
            this.selected.push(item)
          }
        })
      } else {
        const pageIds = new Set(this.paginatedData.map(i => String(i.products_id)))
        this.selected = this.selected.filter(s => !pageIds.has(String(s.products_id)))
      }
    },
    selectAllRows() {
      this.selected = [...this.filteredData]
    },
    clearSelected() {
      this.selected = []
    },
    clearData() {
      this.inputUrl = ''
      this.inputUrlXML = ''
      this.multiUrlConfig.urlListText = ''
      this.respData = []
      this.selected = []
      this.queriedItems = 0
      this.currentPage = 1
      this.fetchSummary.show = false
      if (this.$refs.uploadFileJson) {
        this.$refs.uploadFileJson.reset()
      }
    },
    pageSize(size) {
      localStorage.setItem('itemsPerPage', size)
      this.perPage = parseInt(size, 10)
      this.currentPage = 1
    },
    nextPage(page) {
      this.currentPage = page
    },

    // Parser: JSON
    parseProductsFromJSON(data, pageNum, sourceLabel) {
      let rawList = []
      if (Array.isArray(data)) {
        rawList = data
      } else if (data && Array.isArray(data.data)) {
        rawList = data.data
      } else if (data && Array.isArray(data.products)) {
        rawList = data.products
      } else if (data && typeof data === 'object') {
        const values = Object.values(data)
        const firstArray = values.find(v => Array.isArray(v))
        if (firstArray) rawList = firstArray
      }

      const list = []
      for (let i = 0; i < rawList.length; i += 1) {
        const item = rawList[i]
        let images = []
        if (item.products_image) {
          if (Array.isArray(item.products_image)) {
            images = item.products_image
          } else if (typeof item.products_image === 'string') {
            try {
              images = JSON.parse(item.products_image)
            } catch (e) {
              images = [item.products_image]
            }
          }
        }

        const prodId = item.products_id || item.id || item._id || `ITEM_${pageNum}_${i + 1}`
        list.push({
          products_image: Array.isArray(images) ? images : [],
          products_id: prodId,
          productslang_name: item.productslang_name || item.name || item.title || `Product ${prodId}`,
          productpcategory_pcategory_id: item.pcategory_id || item.productpcategory_pcategory_id
            ? `[${item.pcategory_id || item.productpcategory_pcategory_id}]`
            : '[]',
          products_price: parseFloat(item.products_price || item.price || 0),
          products_weight: parseFloat(item.products_weight || item.weight || 0),
          promotion_discount: parseFloat(item.promotion_discount || item.discount || 0),
          productslang_detail: item.productslang_detail || item.detail || item.description || '',
          company_id: item.company_id || '',
          source_page: sourceLabel || `Page ${pageNum}`,
        })
      }
      return list
    },

    // Parser: XML
    async parseProductsFromXML(xmlText, pageNum, sourceLabel) {
      const json = await parseStringPromise(xmlText, {
        explicitArray: false,
        mergeAttrs: true,
      })
      if (!json) return []

      const companyId = (json.rss && json.rss.company && json.rss.company.id) ? json.rss.company.id : ''
      let products = []
      if (json.rss && json.rss.product && json.rss.product.item) {
        products = json.rss.product.item
      } else if (json.products && json.products.item) {
        products = json.products.item
      }

      if (!Array.isArray(products)) {
        products = products ? [products] : []
      }

      const list = []
      for (let i = 0; i < products.length; i += 1) {
        const item = products[i]
        const isArrayOfId = Array.isArray(item.id)
        const prodId = isArrayOfId ? item.id[0] : item.id
        list.push({
          products_image: item.image
            ? [String(item.image).replace('UploadImagesSmall', 'UploadImages')]
            : [],
          products_id: prodId,
          productslang_name: item.name || `Product ${prodId}`,
          productpcategory_pcategory_id: item.maincate ? `[${item.maincate}]` : '[]',
          products_price: parseFloat(item.price || 0),
          products_weight: parseFloat(item.weight || 0),
          promotion_discount: 0,
          productslang_detail: item.description || '',
          company_id: companyId,
          source_page: sourceLabel || `Page ${pageNum}`,
        })
      }
      return list
    },

    // Deduplication & Aggregation
    mergeAndDeduplicate(allFoundItems, totalPages, successPages, failedPages) {
      const seen = new Set()
      const uniqueItems = []
      let duplicateCount = 0

      for (let i = 0; i < allFoundItems.length; i += 1) {
        const item = allFoundItems[i]
        const key = item.products_id ? String(item.products_id).trim() : null
        if (key && seen.has(key)) {
          duplicateCount += 1
        } else {
          if (key) seen.add(key)
          uniqueItems.push(item)
        }
      }

      this.respData = uniqueItems
      this.queriedItems = uniqueItems.length
      this.currentPage = 1
      this.selected = []

      this.fetchSummary = {
        show: true,
        totalPages,
        successPages,
        failedPages,
        rawTotal: allFoundItems.length,
        duplicateCount,
        uniqueCount: uniqueItems.length,
      }

      if (uniqueItems.length > 0) {
        this.showToast(
          'success',
          'top-right',
          `${this.$t('FETCH_SUCCESS') || 'ดึงข้อมูลสำเร็จ'}! ${uniqueItems.length} ${this.$t('List')} (${duplicateCount} ${this.$t('DUPLICATES_SKIPPED') || 'ตัดตัวซ้ำ'})`,
          'CheckCircleIcon',
          this.$t('GET_DATA') || 'ดึงข้อมูลเรียบร้อย',
        )
      } else {
        this.showToast(
          'warning',
          'top-right',
          this.$t('NOT_FOUND_DATA') || 'ไม่พบข้อมูลสินค้าจากทุกหน้าที่ระบุ',
          'AlertTriangleIcon',
          this.$t('Warning') || 'แจ้งเตือน',
        )
      }
    },

    // Generic Concurrent Fetch Pool Runner
    async runConcurrentTasks(tasks, concurrencyLimit = 3) {
      const results = []
      const executing = []
      for (let i = 0; i < tasks.length; i += 1) {
        if (this.abortFetch) break
        const task = tasks[i]
        const p = Promise.resolve().then(() => task()).then(res => {
          executing.splice(executing.indexOf(p), 1)
          return res
        }).catch(err => {
          executing.splice(executing.indexOf(p), 1)
          return { error: err, failed: true }
        })

        results.push(p)
        executing.push(p)

        if (executing.length >= concurrencyLimit) {
          // eslint-disable-next-line no-await-in-loop
          await Promise.race(executing)
        }
      }
      return Promise.all(results)
    },

    // Mode 1: Fetch Page Range
    async fetchPageRange() {
      const urlTemplate = this.rangeConfig.urlTemplate.trim()
      if (!urlTemplate) {
        this.showToast('danger', 'top-right', 'กรุณาระบุ URL Template', 'XIcon', this.$t('Error'))
        return
      }

      const startPage = Math.max(1, parseInt(this.rangeConfig.startPage, 10) || 1)
      const endPage = Math.max(startPage, parseInt(this.rangeConfig.endPage, 10) || 1)
      const totalPages = endPage - startPage + 1
      const concurrency = Math.min(10, Math.max(1, parseInt(this.rangeConfig.concurrency, 10) || 3))

      this.isFetching = true
      this.abortFetch = false
      this.fetchProgress = {
        current: 0,
        total: totalPages,
        currentUrl: '',
        foundProducts: 0,
        successPages: 0,
        failedPages: 0,
      }

      const pageTasks = []
      for (let page = startPage; page <= endPage; page += 1) {
        const pageNum = page
        pageTasks.push(async () => {
          if (this.abortFetch) return { items: [], pageNum, failed: true }

          // Generate URL for this page
          let pageUrl = urlTemplate
          if (pageUrl.includes('{page}')) {
            pageUrl = pageUrl.replaceAll('{page}', pageNum)
          } else if (pageUrl.includes('{p}')) {
            pageUrl = pageUrl.replaceAll('{p}', pageNum)
          } else {
            pageUrl += `${pageUrl.includes('?') ? '&' : '?'}page=${pageNum}`
          }

          this.fetchProgress.currentUrl = pageUrl

          try {
            let items = []
            if (this.rangeConfig.format === 'xml') {
              const res = await axios.get(pageUrl, { responseType: 'text' })
              items = await this.parseProductsFromXML(res.data, pageNum, `Page ${pageNum}`)
            } else {
              const res = await axios.get(pageUrl)
              items = this.parseProductsFromJSON(res.data, pageNum, `Page ${pageNum}`)
            }

            this.fetchProgress.current += 1
            this.fetchProgress.successPages += 1
            this.fetchProgress.foundProducts += items.length
            return { items, pageNum, failed: false }
          } catch (err) {
            console.error(`Error fetching page ${pageNum}:`, err)
            this.fetchProgress.current += 1
            this.fetchProgress.failedPages += 1
            return { items: [], pageNum, failed: true }
          }
        })
      }

      const results = await this.runConcurrentTasks(pageTasks, concurrency)
      this.isFetching = false

      let allFoundItems = []
      let successCount = 0
      let failedCount = 0

      results.forEach(r => {
        if (r && !r.failed && r.items) {
          allFoundItems = allFoundItems.concat(r.items)
          successCount += 1
        } else {
          failedCount += 1
        }
      })

      this.mergeAndDeduplicate(allFoundItems, totalPages, successCount, failedCount)
    },

    // Mode 2: Fetch Multi-URL List
    async fetchMultiUrlList() {
      const lines = this.multiUrlConfig.urlListText
        .split('\n')
        .map(u => u.trim())
        .filter(u => u.length > 0)

      if (lines.length === 0) {
        this.showToast('danger', 'top-right', 'กรุณาระบุ URL อย่างน้อย 1 ลิงก์', 'XIcon', this.$t('Error'))
        return
      }

      const totalUrls = lines.length
      const concurrency = Math.min(10, Math.max(1, parseInt(this.multiUrlConfig.concurrency, 10) || 3))

      this.isFetching = true
      this.abortFetch = false
      this.fetchProgress = {
        current: 0,
        total: totalUrls,
        currentUrl: '',
        foundProducts: 0,
        successPages: 0,
        failedPages: 0,
      }

      const tasks = lines.map((url, idx) => async () => {
        if (this.abortFetch) return { items: [], idx, failed: true }
        this.fetchProgress.currentUrl = url

        try {
          const isXml = this.multiUrlConfig.format === 'xml'
            || (this.multiUrlConfig.format === 'auto' && (url.includes('.xml') || url.includes('header=xml') || url.includes('format=xml')))

          let items = []
          if (isXml) {
            const res = await axios.get(url, { responseType: 'text' })
            items = await this.parseProductsFromXML(res.data, idx + 1, `URL #${idx + 1}`)
          } else {
            const res = await axios.get(url)
            items = this.parseProductsFromJSON(res.data, idx + 1, `URL #${idx + 1}`)
          }

          this.fetchProgress.current += 1
          this.fetchProgress.successPages += 1
          this.fetchProgress.foundProducts += items.length
          return { items, idx, failed: false }
        } catch (err) {
          console.error(`Error fetching URL #${idx + 1}:`, err)
          this.fetchProgress.current += 1
          this.fetchProgress.failedPages += 1
          return { items: [], idx, failed: true }
        }
      })

      const results = await this.runConcurrentTasks(tasks, concurrency)
      this.isFetching = false

      let allFoundItems = []
      let successCount = 0
      let failedCount = 0

      results.forEach(r => {
        if (r && !r.failed && r.items) {
          allFoundItems = allFoundItems.concat(r.items)
          successCount += 1
        } else {
          failedCount += 1
        }
      })

      this.mergeAndDeduplicate(allFoundItems, totalUrls, successCount, failedCount)
    },

    stopFetching() {
      this.abortFetch = true
      this.isFetching = false
      this.showToast('warning', 'top-right', 'หยุดการดึงข้อมูลตามคำขอ', 'AlertTriangleIcon', 'Stop')
    },

    // Mode 3: Single URL JSON Fetch
    getData() {
      if (!this.inputUrl.trim()) return
      this.show = true
      axios.get(this.inputUrl.trim())
        .then(response => {
          const list = this.parseProductsFromJSON(response.data, 1, 'Page 1')
          this.mergeAndDeduplicate(list, 1, 1, 0)
          this.show = false
        })
        .catch(err => {
          console.error('ERROR MSG:', err)
          this.show = false
          this.showToast('danger', 'top-right', 'เกิดข้อผิดพลาดในการดึงข้อมูล', 'XIcon', this.$t('Error'))
        })
    },

    // Mode 3: Single URL XML Fetch
    getDataXML() {
      if (!this.inputUrlXML.trim()) return
      this.show = true
      axios.get(this.inputUrlXML.trim(), { responseType: 'text' })
        .then(async response => {
          const list = await this.parseProductsFromXML(response.data, 1, 'Page 1')
          this.mergeAndDeduplicate(list, 1, 1, 0)
          this.show = false
        })
        .catch(err => {
          console.error('ERROR XML MSG:', err)
          this.show = false
          this.showToast('danger', 'top-right', 'เกิดข้อผิดพลาดในการอ่าน XML', 'XIcon', this.$t('Error'))
        })
    },

    // Mode 3: File JSON Upload
    updateFiles(input) {
      if (input.target.files && input.target.files[0]) {
        const reader = new FileReader()
        reader.onload = e => {
          try {
            const parsed = JSON.parse(e.target.result)
            const list = this.parseProductsFromJSON(parsed, 1, 'JSON File')
            this.mergeAndDeduplicate(list, 1, 1, 0)
          } catch (err) {
            this.showToast('danger', 'top-right', 'ไฟล์ JSON ไม่ถูกต้อง', 'XIcon', this.$t('Error'))
          }
        }
        reader.readAsText(input.target.files[0])
      }
    },

    // Import Selected Products to Database
    importData() {
      if (this.selected.length === 0) {
        this.showToast('danger', 'top-right', `${this.$t('NOT_SELECT_DATA')}!`, 'XIcon', this.$t('NOT_SELECT_DATA'))
        return
      }

      this.$bvModal
        .msgBoxConfirm(
          `${this.$t('Do you want to import all product') || 'คุณต้องการนำเข้าสินค้า'} ${this.selected.length} ${this.$t('List')}?`,
          {
            title: this.$t('Please Confirm') || 'ยืนยันการนำเข้าข้อมูล',
            cancelVariant: 'outline-secondary',
            okVariant: 'primary',
            okTitle: this.$t('Yes') || 'ตกลง',
            cancelTitle: this.$t('No') || 'ยกเลิก',
            centered: true,
          },
        )
        .then(confirmed => {
          if (confirmed === true) {
            const obj = { dataSelected: this.selected }
            this.show = true
            store
              .dispatch(`${STORE_MODULE_NAME}/import`, obj)
              .then(() => {
                this.showToast(
                  'success',
                  'top-right',
                  `${this.$t('IMPORT_DATA')}! ${this.selected.length} ${this.$t('List')}`,
                  'CheckCircleIcon',
                  this.$t('IMPORT_DATA'),
                )
              })
              .catch(error => {
                this.show = false
                this.$toast({
                  component: ToastificationContent,
                  position: 'bottom-right',
                  props: {
                    title: this.$t('Error'),
                    icon: 'ErrorIcon',
                    variant: 'danger',
                    text: error.response && error.response.data && error.response.data.message
                      ? this.$t(error.response.data.message)
                      : 'Import Failed',
                  },
                })
              })
          }
        })
    },

    // Import All Products to Database
    importAllProduct() {
      if (this.respData.length === 0) {
        this.showToast('danger', 'top-right', `${this.$t('NOT_SELECT_DATA')}!`, 'XIcon', this.$t('NOT_SELECT_DATA'))
        return
      }

      this.$bvModal
        .msgBoxConfirm(
          `${this.$t('Do you want to import all product') || 'คุณต้องการนำเข้าสินค้าทั้งหมด'} ${this.respData.length} ${this.$t('List')}?`,
          {
            title: this.$t('Please Confirm') || 'ยืนยันการนำเข้าข้อมูลทั้งหมด',
            cancelVariant: 'outline-secondary',
            okVariant: 'success',
            okTitle: this.$t('Yes') || 'ตกลง',
            cancelTitle: this.$t('No') || 'ยกเลิก',
            centered: true,
          },
        )
        .then(confirmed => {
          if (confirmed === true) {
            const obj = { dataSelected: this.respData }
            this.show = true
            store
              .dispatch(`${STORE_MODULE_NAME}/import`, obj)
              .then(() => {
                this.showToast(
                  'success',
                  'top-right',
                  `${this.$t('IMPORT_DATA')}! ${this.respData.length} ${this.$t('List')}`,
                  'CheckCircleIcon',
                  this.$t('IMPORT_DATA'),
                )
              })
              .catch(error => {
                this.show = false
                this.$toast({
                  component: ToastificationContent,
                  position: 'bottom-right',
                  props: {
                    title: this.$t('Error'),
                    icon: 'ErrorIcon',
                    variant: 'danger',
                    text: error.response && error.response.data && error.response.data.message
                      ? this.$t(error.response.data.message)
                      : 'Import Failed',
                  },
                })
              })
          }
        })
    },

    showToast(variant, position, text, icon, title) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title,
            icon,
            text,
            variant,
          },
        },
        { position },
      )
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.per-page-selector {
  display: inline-block;
  margin-left: 0.25rem;
  margin-right: 0.5rem;
}

.b-col-custom {
  margin-top: 0.5rem;
}
</style>
